import { useEffect, useRef, useState } from "react";
import styles from "./searchModalMobile.module.scss";

import Input from "@Components/Input";
import ArrowIconBlack from "@Icons/ArrowIconBlack";
import DefaultSuggestionsMobile from "./components/DefaultSuggestionsMobile";
import { getSearchResult } from "./data/helper";
import SearchResultList from "./components/SearchResultList";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import Portal from "@Components/Portal";
import SearchIcon from "@Icons/SearchIcon";
import { useHistory } from "hooks/useHistory";
import customHistoryPush from "@Utils/customHistoryPush";
import useTranslationHook from "hooks/useTranslationHook";
import DropDownLoader from "@Components/DropdownLoader";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import { logSingularEvent } from "@Utils/appAnalytics";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import useIsIframe from "hooks/useIsIframe";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import appStyles from "@App/app.module.scss";

const SearchModalMobile = ({
  setShowSearchModal,
  isDesktop = false,
  fromSearchPage = false,
  isBlogSearch = false,
  isNewsSearch = false,
  isExamsSearch = false,
}: {
  setShowSearchModal?: any;
  isDesktop?: boolean;
  fromSearchPage?: boolean;
  isBlogSearch?: boolean;
  isNewsSearch?: boolean;
  isExamsSearch?: boolean;
}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any>([]);
  const [inputValue, setInputValue] = useState("");
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const {
    isWhitelabel,
    isDemoSubdomain,
    partner_uuid,
    isRestrictedSearchEnabled,
    partner_id,
    whitelabelColors,
  } = useWhitelabel();
  const isMiniProgram = useIsMiniProgram();
  const { isIframe } = useIsIframe();
  const useJavaApi = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.USE_JAVA_API_SEARCH);

  const isContentSearch = isBlogSearch || isNewsSearch;

  const { t } = useTranslationHook();

  const history = useHistory();

  const timeout: any = useRef();
  const inputLength = inputValue.length;
  const modalRef = useRef<any>(null);

  const handleInputValueChange = (e: any) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    const value = e.trim().toLowerCase();
    setInputValue(e);

    // For content search (blog/news), don't fetch real-time results
    if (isContentSearch || isExamsSearch) {
      return;
    }

    if (value.length > 1) {
      setLoading(true);
      timeout.current = setTimeout(async () => {
        try {
          const res: any = await getSearchResult(
            e,
            isWhitelabel,
            isRestrictedSearchEnabled,
            partner_id,
          );
          setItems(res?.data?.data || []);
        } finally {
          setLoading(false);
        }
      }, 200);
    } else {
      setItems([]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (isDesktop && modalRef.current && !modalRef.current.contains(event.target)) {
        setShowSearchResult(false);
        setSelectedIndex(-1);
      }
    };

    const handleKeyPres = (e: any) => {
      if (e.keyCode === 27) {
        // Esc
        setShowSearchResult(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyPres);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyPres);
    };
  }, []);

  // iOS Safari scroll position fix: when the search modal opens on mobile,
  // Safari can shift the underlying page scroll. Save position on mount
  // and restore on unmount so the page doesn't appear cut off when going back.
  useEffect(() => {
    if (isDesktop) return undefined;
    const savedScrollY = window.scrollY;

    // Apply position:fixed body lock to prevent iOS Safari from shifting scroll
    document.body.classList.add(appStyles.bodyLockedFixed);
    document.body.style.top = `-${savedScrollY}px`;

    return () => {
      document.body.classList.remove(appStyles.bodyLockedFixed);
      document.body.style.top = "";
      window.scrollTo(0, savedScrollY);
    };
  }, []);

  const isSuggestionsVisible = inputLength === 0;

  const handleContentSearch = () => {
    if (inputValue.trim().length > 0) {
      const basePath = isNewsSearch ? "/news" : "/blog";
      const searchUrl = `${basePath}/search?query=${encodeURIComponent(inputValue.trim())}`;

      customHistoryPush(history, searchUrl, false);
      setShowSearchModal?.(false);
      setShowSearchResult?.(false);
    }
  };

  const handleExamsSearch = () => {
    if (inputValue.trim().length > 0) {
      const query = inputValue.trim().toLowerCase();
      const examMap: Record<string, string> = {
        toefl: "toefl-exam",
        gmat: "gmat-exam",
        gre: "gre-exam",
        ielts: "ielts-exam",
        sat: "sat-exam",
      };
      const examSlug = examMap[query];
      const searchUrl = examSlug ? `/exams/${query}/${examSlug}` : `/exams`;
      customHistoryPush(history, searchUrl, false);
      setShowSearchModal?.(false);
      setShowSearchResult?.(false);
    }
  };

  const handleOnClick = (data: any) => {
    let redirectLink = "";

    if (isContentSearch) {
      const basePath = isNewsSearch ? "/news" : "/blog";
      redirectLink = `${basePath}/post/${data?.canonical_name}`;
    } else if (isExamsSearch) {
      redirectLink = `/exams/${data?.canonical_name?.split("-")?.[0]}/${data?.canonical_name}`;
    } else {
      redirectLink = `/places/${data?.type != "inventory" ? "search/" : ""}${data?.canonical_name}`;
      if (isWhitelabel) {
        redirectLink = convertToWhitelabelURL(redirectLink, isDemoSubdomain, partner_uuid);
      }
    }

    gtagEventEmitter({
      action: "clicked_searched_result",
      category: isContentSearch ? "content_search" : PAGE.SEARCH,
      label: `${redirectLink}, 1, ${inputValue}`,
      canonical_name: data.canonical_name,
      use_java_api: useJavaApi,
    });

    // For whitelabel in iframe, open inventory in new tab
    if (isWhitelabel && isIframe && data.type === "inventory") {
      window.open(redirectLink, "_blank", "noopener,noreferrer");
      setShowSearchModal?.(false);
      setShowSearchResult?.(false);
      setInputValue?.("");
    } else {
      customHistoryPush(history, redirectLink, fromSearchPage);
      setShowSearchModal?.(false);
      setShowSearchResult?.(false);
      setInputValue?.("");
    }
    logSingularEvent("sngSearch", { sngAttrSearchString: data?.name });
  };

  const getItemProps: any = ({ data }: { data: any }) => ({
    onClick: () => handleOnClick(data),
  });

  const handleKeyDown = (e: any) => {
    const listItemHeight = 73.29;
    if (e.key === "Enter") {
      if (isContentSearch) {
        // For content search, navigate to search results page
        e.target.blur();
        handleContentSearch();
        return;
      }

      if (isExamsSearch) {
        // For exams search, navigate to exam page
        e.target.blur();
        handleExamsSearch();
        return;
      }

      if (items && items?.length > 0) {
        e.target.blur();
        if (selectedIndex < 0) {
          handleOnClick(items?.[0]);
        } else {
          handleOnClick(items?.[selectedIndex]);
        }
      }
    }
    if (items?.length) {
      const container: any = document.getElementById(`searchresultUl`);
      if (e.key == "ArrowDown" && items.length > selectedIndex + 1) {
        setSelectedIndex((si) => si + 1);
        const itemVisible = isVisible(
          document.getElementById(`searchListItem${selectedIndex + 2}`),
          container,
        );
        if (!itemVisible) container?.scrollTo(0, (selectedIndex - 2) * listItemHeight);
      } else if (e.key == "ArrowUp" && selectedIndex > 0) {
        setSelectedIndex((si) => si - 1);
        const itemVisible = isVisible(
          document.getElementById(`searchListItem${selectedIndex - 2}`),
          container,
        );
        if (!itemVisible) container?.scrollTo(0, (selectedIndex - 1) * listItemHeight);
      }
    }
  };

  const isVisible = (ele: any, container: any) => {
    const eleTop = ele?.offsetTop;
    const eleBottom = eleTop + ele?.clientHeight;

    const containerTop = container?.scrollTop;
    const containerBottom = containerTop + container?.clientHeight;

    // The element is fully visible in the container
    return (
      (eleTop >= containerTop && eleBottom <= containerBottom) ||
      // Some part of the element is visible in the container
      (eleTop < containerTop && containerTop < eleBottom) ||
      (eleTop < containerBottom && containerBottom < eleBottom)
    );
  };
  const getSearchPlaceholder = () => {
    if (isContentSearch) {
      return t(isNewsSearch ? "news.header.searchnews" : "blog.header.searchblogs");
    }
    if (isExamsSearch) {
      return t("exams.header.searchexams");
    }
    return t("home.components.searchmodalmobile.placeholder");
  };

  const renderSearchIcon = () => {
    if (isContentSearch) {
      return (
        <button
          type="button"
          className={styles.searchIcon}
          onClick={handleContentSearch}
          style={{ cursor: "pointer", border: "none", padding: 0 }}
        >
          <SearchIcon
            size="12"
            fill={isWhitelabel ? whitelabelColors?.primary_text_color : "#fff"}
          />
        </button>
      );
    }

    if (isExamsSearch) {
      return (
        <button
          type="button"
          className={styles.searchIcon}
          onClick={handleExamsSearch}
          style={{ cursor: "pointer", border: "none", padding: 0 }}
        >
          <SearchIcon
            size="12"
            fill={isWhitelabel ? whitelabelColors?.primary_text_color : "#fff"}
          />
        </button>
      );
    }

    return (
      <i className={styles.searchIcon}>
        <SearchIcon size="12" fill={isWhitelabel ? whitelabelColors?.primary_text_color : "#fff"} />
      </i>
    );
  };

  const handleOnFocus = () => {
    if (isDesktop && !isContentSearch) {
      setShowSearchResult(true);
    }
    gtagEventEmitter({
      action: "clicked_search_bar",
      category: fromSearchPage ? PAGE.SEARCH : PAGE.PROPERTY,
      label: "search_bar",
      use_java_api: useJavaApi,
    });
  };

  const renderSearchResult = () => (
    <div className={styles.searchListContainer}>
      {inputLength < 2 &&
        inputLength > 0 &&
        items?.length === 0 &&
        !isContentSearch &&
        !isExamsSearch && (
          <div className={styles.defTextContainer}>
            <i className={`icon-keyboard ${styles.keyboardIcon}`} />
            <div data-testid="search-message" className={styles.defText}>
              {t("You can find your accommodation more easily if you add")} {2 - inputLength}{" "}
              {t(`more alphabet${2 - inputLength > 1 ? "s" : ""}`)}
            </div>
          </div>
        )}
      {isSuggestionsVisible && !isContentSearch && !isExamsSearch && (
        <DefaultSuggestionsMobile
          setShowSearchModal={isDesktop ? setShowSearchResult : setShowSearchModal}
          gtagEventCategory={PAGE.SEARCH}
          fromSearchPage={fromSearchPage}
        />
      )}
      {!isSuggestionsVisible && loading && !(inputLength < 2) ? (
        <DropDownLoader />
      ) : (
        <SearchResultList
          selectedIndex={selectedIndex}
          items={items}
          getItemProps={getItemProps}
          dataTestId="mobile-search-suggestions"
        />
      )}
    </div>
  );

  return (
    <div className={formatClassNames(isDesktop && styles.desktop)}>
      <div className={styles["searchContainer"]}>
        {!isDesktop && !isMiniProgram && (
          <div
            className={styles["back-btn"]}
            role="none"
            onClick={() => {
              setShowSearchModal(false);
            }}
            data-testid="search-modal-back-button"
          >
            <ArrowIconBlack />
          </div>
        )}
        <Input
          type="text"
          label=""
          className={styles["searchInput"]}
          placeholder={getSearchPlaceholder()}
          showPlaceholder
          value={inputValue}
          onChange={handleInputValueChange}
          showClearInnput
          autoFocus={!isDesktop}
          onFocus={() => handleOnFocus()}
          onKeyDown={handleKeyDown}
          dataTestId="mobile-search-input"
        />
        {isDesktop && renderSearchIcon()}
      </div>
      {!isDesktop && !isContentSearch && !isExamsSearch && renderSearchResult()}
      {isDesktop && showSearchResult && !isContentSearch && !isExamsSearch && (
        <div className={styles.desktopModal} id="searchresultUl" ref={modalRef}>
          <Portal>
            <div
              role="none"
              onClick={() => setShowSearchResult(false)}
              className={styles.overlay}
            />
          </Portal>
          {renderSearchResult()}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(SearchModalMobile, {
  componentName: "SearchModalMobile-Global",
  showForChina: true,
});
